// T056: Defense Phase Handler

import type { GameState, CardDefinition, CombatResult } from '../../types';
import { CombatResolver } from './CombatResolver';
import { CombatSystem } from '../systems/CombatSystem';
import { getLogger } from '../../utils/logger';

const log = getLogger('DefensePhaseHandler');

export interface DefenseResult {
  state: GameState;
  combatOccurred: boolean;
  destroyedUnits: string[];
  upgradedUnits: { from: string; to: string; unitId: string }[];
  combatResults: CombatResult[];
  messages: string[];
}

/**
 * Execute the defense phase.
 * This resolves any pending combat from the previous turn's attack declarations.
 */
export function execute(
  state: GameState,
  definitions: Map<string, CardDefinition>
): DefenseResult {
  log.debug('execute called');
  log.debug('Attack declarations:', state.attackDeclarations.length, state.attackDeclarations);
  
  // Check if there are pending attacks to resolve
  if (!CombatResolver.hasPendingCombat(state)) {
    log.debug('No pending combat, skipping');
    return {
      state: { ...state },
      combatOccurred: false,
      destroyedUnits: [],
      upgradedUnits: [],
      combatResults: [],
      messages: [],
    };
  }
  
  log.debug('Pending combat detected, resolving...');

  // 使用新的 CombatSystem
  const combatSystem = new CombatSystem({
    definitions,
    enableAutoUpgrade: true,
    enablePassengerEjection: true,
  });

  const result = combatSystem.resolveAllCombat(state);

  // Clear attack declarations after combat is resolved
  const finalState = {
    ...result.state,
    attackDeclarations: [],
  };

  return {
    state: finalState,
    combatOccurred: true,
    destroyedUnits: result.destroyedUnits,
    upgradedUnits: result.upgradedUnits,
    combatResults: result.combatResults,
    messages: result.messages,
  };
}

/**
 * Check if defense phase has any pending combat.
 */
export function hasPendingCombat(state: GameState): boolean {
  return CombatResolver.hasPendingCombat(state);
}

/**
 * Get preview of pending combat.
 */
export function getCombatPreviews(
  state: GameState,
  definitions: Map<string, CardDefinition>
) {
  return state.attackDeclarations.map((declaration) =>
    CombatResolver.getCombatPreview(state, declaration.attackerPosition, definitions)
  ).filter((preview) => preview !== null);
}

// Re-export as object for backwards compatibility
export const DefensePhaseHandler = {
  execute,
  hasPendingCombat,
  getCombatPreviews,
};
